import { Link } from 'react-router-dom'
import type { BoardCard, BoardColumnId, BoardLane, BoardResponse } from '@/services/api/superadminDashboard'

const COLS: { id: BoardColumnId; label: string }[] = [
  { id: 'open', label: 'Offen' },
  { id: 'in_progress', label: 'In Arbeit' },
  { id: 'waiting', label: 'Wartet' },
  { id: 'done', label: 'Heute erledigt' },
]

function actionsFor(column: BoardColumnId, wipFull: boolean): { column: BoardColumnId; label: string; disabled?: boolean }[] {
  if (column === 'open') {
    return [
      { column: 'in_progress', label: 'In Arbeit', disabled: wipFull },
      { column: 'waiting', label: 'Warten' },
      { column: 'done', label: 'Erledigt' },
    ]
  }
  if (column === 'in_progress') {
    return [
      { column: 'open', label: 'Nach Offen' },
      { column: 'waiting', label: 'Warten' },
      { column: 'done', label: 'Erledigt' },
    ]
  }
  if (column === 'waiting') {
    return [
      { column: 'open', label: 'Nach Offen' },
      { column: 'in_progress', label: 'In Arbeit', disabled: wipFull },
      { column: 'done', label: 'Erledigt' },
    ]
  }
  return [{ column: 'open', label: 'Nach Offen' }]
}

function CardView({
  card,
  column,
  wipFull,
  moving,
  onMove,
}: {
  card: BoardCard
  column: BoardColumnId
  wipFull: boolean
  moving: string | null
  onMove: (key: string, column: BoardColumnId) => void
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <Link to={card.href} className="block text-left w-full">
        <p className="text-sm font-semibold text-gray-900">{card.title}</p>
        <p className="mt-1 text-xs text-gray-500">{card.meta}</p>
        <div className="mt-2 flex gap-2 text-xs text-gray-400">
          {card.count != null && <span>{card.count}</span>}
          {card.age_days != null && card.age_days > 0 && <span>{card.age_days} T</span>}
          {card.expires_at && (
            <span>
              {new Date(card.expires_at).toLocaleString('de-CH', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          )}
        </div>
      </Link>
      <div className="mt-2 flex flex-wrap gap-1">
        {actionsFor(column, wipFull).map(a => (
          <button
            key={a.column}
            type="button"
            disabled={Boolean(a.disabled) || moving === card.key}
            title={a.disabled ? 'In Arbeit ist voll (Limit 2)' : undefined}
            onClick={e => {
              e.stopPropagation()
              onMove(card.key, a.column)
            }}
            className="text-xs px-2 py-1 rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 disabled:opacity-40"
          >
            {a.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function LaneRow({
  lane,
  wipFull,
  moving,
  onMove,
}: {
  lane: BoardLane
  wipFull: boolean
  moving: string | null
  onMove: (key: string, column: BoardColumnId) => void
}) {
  return (
    <div className="mb-6">
      <h2 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">{lane.label}</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {COLS.map(col => (
          <div key={col.id} className="rounded-lg bg-gray-50 border border-gray-100 p-2 min-h-[88px]">
            <div className="flex justify-between text-xs font-semibold text-gray-500 mb-2 px-1">
              <span>{col.label}</span>
              <span>{lane.columns[col.id]?.length ?? 0}</span>
            </div>
            <div className="space-y-2">
              {(lane.columns[col.id] ?? []).map(card => (
                <CardView
                  key={card.key}
                  card={card}
                  column={col.id}
                  wipFull={wipFull}
                  moving={moving}
                  onMove={onMove}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function PlatformBoard({
  board,
  moving,
  onMove,
}: {
  board: BoardResponse
  moving: string | null
  onMove: (key: string, column: BoardColumnId) => void
}) {
  if (board.lanes.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-6 py-16 text-center text-gray-500">
        {board.reason === 'no_school' ? 'Keine Schule zugeordnet.' : 'Keine offenen Plattform-Arbeiten.'}
      </div>
    )
  }
  const wipFull = board.wip_count >= board.wip_limit
  return (
    <div>
      {board.lanes.map(lane => (
        <LaneRow key={lane.id} lane={lane} wipFull={wipFull} moving={moving} onMove={onMove} />
      ))}
    </div>
  )
}
